// Export signatures below are typed explicitly for TS consumers.
import axios from "axios";
import _, { get, sortBy } from "lodash";
import queryString from "query-string";
import toast from "react-hot-toast";
import { trackPromise } from "react-promise-tracker";

import type {
  AxleAiModel,
  BatchGenerateIgnitionSessionsRequest,
  BatchGenerateIgnitionSessionsResponse,
  Carrier,
} from "@platform/automatic-api";

import { getAccessJwtToken } from "./auth";
import type {
  AxleAiChat,
  AxleAiChatSummary,
  AxleAiMessage,
  PendingAction,
  SupportEmailTopic,
} from "./axleAi/types";
import {
  MAX_POLL_DURATION_MS,
  POLICY_ACTION_STATUS,
  POLICY_ACTION_TOAST_MESSAGES,
  POLL_INTERVAL_MS,
} from "./constants";

/** Standard [data, error] tuple returned by callAxleService helpers. */
export type AxleServiceError = {
  code: number | null;
  message: string | null;
};

export type AxleServiceResult<T> = [T | null, AxleServiceError | null];

export type GenerateSignedUrlData = {
  signedUrl: string;
  logoUrl: string;
  expiresInSeconds?: number;
};

export type PolicyActionCompletionStatus = {
  id: string;
  completionStatus: string;
  status?: string;
};

/** Preserves HTTP status codes for fetch helpers that throw instead of returning tuples. */
export class HttpError extends Error {
  status: number;

  constructor(message: any, status: any = 500) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

const local = false;
export const automaticBaseUrl = local
  ? "http://localhost:4000/dev"
  : `https://${process.env.REACT_APP_STAGE === "prod"
    ? "api"
    : process.env.REACT_APP_STAGE
  }.axle.insure/automatic`;

const baseUrl = automaticBaseUrl;

const fetchClient = async (url: any, options: any = {}) => {
  try {
    const response = await fetch(url, options && options);
    const raw = await response.text();
    let body: any = {};
    if (raw) {
      try {
        body = JSON.parse(raw);
      } catch {
        console.error("Non-JSON response", {
          url,
          status: response.status,
          snippet: raw.slice(0, 200),
        });
        return [
          null,
          {
            code: response.status,
            message:
              response.status === 404
                ? "API route not found — deploy Automatic with /clients/logo or check the request URL."
                : `Server returned non-JSON (${response.status}). See console for details.`,
          },
        ];
      }
    }

    if (!response.ok) {
      return [
        null,
        {
          code: get(response, "status", 500),
          message:
            get(body, "message", null) ?? `Request failed (${response.status})`,
        },
      ];
    }

    return [body.data, null];
  } catch (error: any) {
    console.error("Request failed", { url, error });
    return [
      null,
      {
        code: null,
        message: get(error, "message", "Network error"),
      },
    ];
  }
};

/**
 * Axios-based [data, error] client — port of `axiosClient` in
 * stacks/platform/ignition/src/lib/axle.ts so requests like presigned S3 PUTs
 * share the same tuple contract as fetchClient.
 */
const axiosClient = async (url: string, options = {}) => {
  try {
    const config = {
      url,
      ...options,
    };
    const response = await axios.request(config);

    const data = get(response, "data.data");

    if (get(response, "status") !== 200) {
      return [
        null,
        {
          code: get(response, "status", 500),
          message: get(data, "message", null),
        },
      ];
    }

    return [data, null];
  } catch (error: any) {
    // If the error is a network error, prompt the user with a retry
    if (axios.isAxiosError(error) && get(error, "code") === "ERR_NETWORK") {
      console.error("Connection interrupted");
      return [null, { code: null, message: "Connection interrupted" }];
    }

    // Prefer the API response message when present; fall back to the axios error
    const errorMessage = get(
      error,
      "response.data.message",
      get(error, "message", null)
    );
    const errorCode = get(error, "response.status", get(error, "status", null));
    return [null, { code: errorCode, message: errorMessage }];
  }
};

export const callAxleService = async (
  url: any,
  options: any = {},
  httpClient: any = "fetch"
) => {
  switch (httpClient) {
    case "axios":
      return await axiosClient(url, options);
    case "fetch":
    default:
      return await fetchClient(url, options);
  }
};

export const uploadDocument = async (
  signedUrl: any,
  fileData: any,
  contentType?: string
): Promise<any> => {
  return await callAxleService(
    signedUrl,
    {
      method: "put",
      data: fileData,
      headers: { "Content-Type": contentType || "application/octet-stream" },
    },
    "axios"
  );
};

/**
 * It creates a new ignition session for a user
 * @param userId - The user's id
 * @param clientId - The client's id
 * @param templateId - Optional ignition config template ID
 */
export const createIgnitionSession = async (userId: any, clientId: any, templateId?: any) => {
  return await callAxleService(`${baseUrl}/users/${userId}/ignition`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({
      ...(templateId && { templateId }),
    }),
  });
};

/**
 * Creates an ignition session for the dashboard
 * "Complete Ignition on behalf of user" action.
 * @param clientId - The client's id
 * @param templateId - Optional ignition config template ID
 */
export const openIgnitionSession = async (clientId: any, templateId?: any) => {
  return await callAxleService(`${baseUrl}/ignition`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({
      ...(templateId && { templateId }),
    }),
  });
};

/**
 * Fetches ignition client config templates the client may use (see automatic
 * `getClientIgnitionConfigTemplates` / DMV terminology).
 */
export const getClientIgnitionConfigTemplates = async (clientId: any) => {
  return await callAxleService(`${baseUrl}/ignition/templates`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  });
};

/**
 * It fetches the policy report from the API and returns the data with the report URL
 * @param policyId - The id of the policy you want to get the report for.
 * @returns The policy report data containing the URL to the report
 */
export const getPolicyReport = async (
  policyId: any,
  clientId: any,
  options: any = {}
) => {
  const queryString =
    Object.keys(options).length > 0 ? `?${new URLSearchParams(options)}` : "";
  try {
    const res = await trackPromise(
      fetch(`${baseUrl}/policies/${policyId}/report${queryString}`, {
        method: "get",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: await getAccessJwtToken(),
          "x-client-id": clientId,
        },
      })
    );

    const body = await res.json();

    if (!res.ok) {
      throw new HttpError(body.message || "Request failed", res.status);
    }
    return body.data;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * It takes an ignition token, sends it to the server, and returns the session data
 * @param ignitionToken - The token that was generated when the ignition session was created.
 * @param clientId - The client's id
 * @returns The ignition session is being returned.
 */
export const getIgnitionSession = async (ignitionToken: any, clientId: any) => {
  return await callAxleService(`${baseUrl}/ignition/${ignitionToken}`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  });
};

/**
 * It fetches all the ignition sessions from the database, and returns them in an array
 * @returns An array of ignition sessions.
 */
export const getIgnitionSessions = async (
  { start, end }: any,
  clientId: any
) => {
  let currentLastEvaluatedKey: any;
  let allIgnitionSessions: any[] = [];

  try {
    do {
      const response = await fetch(
        `${baseUrl}/ignition?${queryString.stringify({
          start,
          end,
          lastEvaluatedKey: currentLastEvaluatedKey,
        })}`,
        {
          method: "get",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            "x-client-id": clientId,
            Authorization: await getAccessJwtToken(),
          },
        }
      );

      if (!response.ok) {
        const body = await response.json();
        throw new HttpError(body.message || "Request failed", response.status);
      }

      const { ignitionSessions, lastEvaluatedKey } = await response.json();

      currentLastEvaluatedKey = lastEvaluatedKey;
      allIgnitionSessions = [...allIgnitionSessions, ...ignitionSessions];
    } while (currentLastEvaluatedKey);

    const sortedIgnitionSessions = allIgnitionSessions.sort(
      (a: any, b: any) => {
        return a.createdAt > b.createdAt
          ? -1
          : a.createdAt < b.createdAt
            ? 1
            : 0;
      }
    );

    return sortedIgnitionSessions;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * Fetches metrics data for analytics dashboard
 * @param {string} start - Start date in YYYY-MM-DD format
 * @param {string} end - End date in YYYY-MM-DD format
 * @returns {Promise<any>} Metrics data for the specified date range
 */
export const getClientMetrics = async (clientId: any, start: any, end: any) => {
  const url = `${baseUrl}/metrics/clients?${queryString.stringify({
    start,
    end,
  })}`;
  const options = {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  };
  return await callAxleService(url, options);
};

/**
 * Fetches platform metrics for all destination clients
 * @param {string} clientId - The platform client ID
 * @param {string} start - Start date in YYYY-MM-DD format
 * @param {string} end - End date in YYYY-MM-DD format
 * @param {string} [clientGroup] - Optional client group filter (comma-separated)
 * @param {string} [customDedupeKey] - Optional custom deduplication key
 * @returns {Promise<any>} Platform metrics data for all destination clients
 */
export const getPlatformMetrics = async (
  clientId: any,
  start: any,
  end: any,
  clientGroup: any = null,
  customDedupeKey: any = null
) => {
  const params = _.pickBy(
    { start, end, clientGroup, customDedupeKey },
    (v: any) => !_.isNil(v)
  );

  const url = `${baseUrl}/metrics/platforms?${queryString.stringify(params)}`;
  const options = {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  };
  return await callAxleService(url, options);
};

/**
 * It creates a user
 * @param user - The user object that we want to create.
 * @param clientId - The client's id
 * @returns The user object
 */
export const createUser = async (user: any, clientId: any) => {
  try {
    const res = await trackPromise(
      fetch(`${baseUrl}/users`, {
        method: "post",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          "x-client-id": clientId,
          Authorization: await getAccessJwtToken(),
        },
        body: JSON.stringify(user),
      })
    );

    const body = await res.json();

    if (!res.ok) {
      throw new HttpError(body.message || "Request failed", res.status);
    }
    return body;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * Submits one Filefeed CSV chunk (up to 100 rows) to the automatic batch endpoint, which
 * fans each row out onto SQS for asynchronous ignition session generation. Throws HttpError on
 * failure so the Filefeed importer routes it through onError (and retries the chunk once).
 * @param payload - Chunk payload from buildBatchGenerateIgnitionSessionsPayload
 * @param clientId - The client id
 */
export const batchGenerateIgnitionSessions = async (
  payload: BatchGenerateIgnitionSessionsRequest,
  clientId: string
): Promise<BatchGenerateIgnitionSessionsResponse> => {
  try {
    const res = await fetch(`${baseUrl}/ignition/import`, {
      method: "post",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": clientId,
        Authorization: await getAccessJwtToken(),
      },
      body: JSON.stringify(payload),
    });

    const body = await res.json();

    if (!res.ok) {
      throw new HttpError(body.message || "Request failed", res.status);
    }
    return body as BatchGenerateIgnitionSessionsResponse;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * It fetches a user from the backend and returns the user object
 * @param userId - The user's id
 * @param clientId - The client's id
 * @returns The user object
 */
export const getUser = async (userId: any, clientId: any) => {
  return await callAxleService(`${baseUrl}/users/${userId}`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  });
};

/**
 * It fetches all users from the database, one page at a time, until there are no more pages to fetch
 * @param clientId - The client's id
 * @returns An array of users.
 */
export const getUsers = async (clientId: any) => {
  let currentLastEvaluatedKey: any;
  let allUsers: any[] = [];

  try {
    do {
      // const encodedValue = encodeURIComponent(
      //   JSON.stringify(currentLastEvaluatedKey)
      // );
      // const currentLastEvaluatedKeyParameter = `lastEvaluatedKey=${encodedValue}`;

      const response = await trackPromise(
        fetch(
          `${baseUrl}/users?${queryString.stringify({
            lastEvaluatedKey: currentLastEvaluatedKey,
          })}`,
          {
            method: "get",
            mode: "cors",
            headers: {
              "Content-Type": "application/json",
              "x-client-id": clientId,
              Authorization: await getAccessJwtToken(),
            },
          }
        )
      );

      if (!response.ok) {
        const body = await response.json();
        throw new HttpError(body.message || "Request failed", response.status);
      }

      const { users, lastEvaluatedKey } = await response.json();

      currentLastEvaluatedKey = lastEvaluatedKey;
      allUsers = [...allUsers, ...users];
    } while (currentLastEvaluatedKey);

    const sortedUsers = allUsers.sort((a: any, b: any) => {
      return a.createdAt > b.createdAt ? -1 : a.createdAt < b.createdAt ? 1 : 0;
    });

    return sortedUsers;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * It fetches all users from the database, one page at a time, until there are no more pages to fetch
 * @returns An array of users.
 */
export const getTeam = async (clientId: any): Promise<any> => {
  try {
    const response = await trackPromise(
      fetch(`${baseUrl}/team`, {
        method: "get",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          "x-client-id": clientId,
          Authorization: await getAccessJwtToken(),
        },
      })
    );

    if (!response.ok) {
      const body = await response.json();
      throw new HttpError(body.message || "Request failed", response.status);
    }

    const { members } = await response.json();

    //By default key "role" only exists if user is admin
    const membersWithRole = _.map(members, (obj: any) =>
      _.defaults({ ...obj }, { role: "member" })
    );

    const sortedMembers = sortBy(membersWithRole, "name");

    return sortedMembers;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * Sends an invite to a new team member.
 * @param member - The team member object you want to invite
 * @returns The invited team member.
 */
export const inviteTeamMember = async (member: any, clientId: any) => {
  try {
    const res = await trackPromise(
      fetch(`${baseUrl}/team`, {
        method: "post",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          "x-client-id": clientId,
          Authorization: await getAccessJwtToken(),
        },
        body: JSON.stringify(member),
      })
    );

    const body = await res.json();

    if (!res.ok) {
      throw new HttpError(body.message || "Request failed", res.status);
    }
    return body;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * Disables a team member.
 * @param {Object} member - The team member object with email and role
 * @param {string} clientId - The client ID
 * @returns The disabled user data
 */
export const disableTeamMember = async (member: any, clientId: any) => {
  return await callAxleService(`${baseUrl}/team/disable`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({ email: member.email, role: member.role }),
  });
};

/**
 * Enables a team member.
 * @param {Object} member - The team member object with email and role
 * @param {string} clientId - The client ID
 * @returns The enabled user data
 */
export const enableTeamMember = async (member: any, clientId: any) => {
  return await callAxleService(`${baseUrl}/team/enable`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({ email: member.email, role: member.role }),
  });
};

/**
 * Updates a team member's role/permissions.
 * @param {string} userId - The user ID to update
 * @param {string} role - The new role (e.g., "admin", "member")
 * @param {string} clientId - The client ID
 * @returns The updated user data
 */
export const updateTeamMemberRole = async (
  member: any,
  oldRole: any,
  newRole: any,
  clientId: any
) => {
  return await callAxleService(`${baseUrl}/team/role`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({ email: member.email, oldRole, newRole }),
  });
};

/**
 * Resends a dashboard invite to a team member.
 * @param {Object} member - The team member object with email, name, etc.
 * @param {string} clientId - The client ID
 * @returns The invite response
 */
export const resendTeamMemberInvite = async (member: any, clientId: any) => {
  return await callAxleService(`${baseUrl}/team/invite/resend`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({
      name: member.name,
      email: member.email,
      role: member.role,
    }),
  });
};

/**
 * It fetches all carriers from the database
 * @returns An array of carriers.
 */
export const getCarriers = async (): Promise<Carrier[]> => {
  try {
    const response = await fetch(`${baseUrl}/carriers`, {
      method: "get",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: await getAccessJwtToken(),
      },
    });

    if (!response.ok) {
      const body = await response.json();
      throw new HttpError(body.message || "Request failed", response.status);
    }

    const carriers = await response.json();

    // Custom comparator function to decrease rank of undefined
    const shareComparator = (item: any) => {
      if (item.share === undefined) {
        // Assign a low value to items with undefined share
        return Number.MIN_SAFE_INTEGER;
      }
      return item.share;
    };

    // Sort carriers by user portal status, rank, and share
    const sortedCarriers = _(carriers)
      .orderBy(
        ["userPortalStatus", "rank", shareComparator],
        ["asc", "asc", "desc"]
      )
      .value();

    return sortedCarriers;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * Fetches enriched policies with streaming support
 * @param {string} clientId - The client's id
 * @param {Function|null} onPolicyBatch - Optional callback for streaming policies as they arrive
 * @returns {Promise<{policies: any[], totalCount: number, isComplete: boolean}>}
 */
export const getEnrichedPolicies = async (
  clientId: any,
  onPolicyBatch: any = null
) => {
  let currentLastEvaluatedKey: any;
  let allPolicies: any[] = [];
  let hasMorePages = true;

  try {
    do {
      const response = await callAxleService(
        `${baseUrl}/policies/enriched?${queryString.stringify({
          limit: 400,
          lastEvaluatedKey: currentLastEvaluatedKey,
        })}`,
        {
          method: "get",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            "x-client-id": clientId,
            Authorization: await getAccessJwtToken(),
          },
        }
      );

      const [result, error] = response;
      if (error) {
        currentLastEvaluatedKey = undefined;
        throw error;
      }

      const { policies, lastEvaluatedKey } = result;

      currentLastEvaluatedKey = lastEvaluatedKey;
      allPolicies = [...allPolicies, ...policies];

      // If callback is provided, stream the policies as they arrive
      // We'll sort them in real-time on the frontend to maintain progressive loading
      if (
        onPolicyBatch &&
        typeof onPolicyBatch === "function" &&
        policies &&
        policies.length > 0
      ) {
        onPolicyBatch(policies, allPolicies, hasMorePages);
      }

      // Check if this is the last page
      hasMorePages = !!lastEvaluatedKey;
    } while (currentLastEvaluatedKey);

    return {
      policies: allPolicies,
      totalCount: allPolicies.length,
      isComplete: true,
    };
  } catch (error: any) {
    console.error(error);
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * It fetches all policies from the API, sorts them by date, and returns them
 * @returns An array of policies
 */
export const getPolicies = async (clientId: any) => {
  let currentLastEvaluatedKey: any;
  let allPolicies: any[] = [];

  try {
    do {
      const response = await fetch(
        `${baseUrl}/policies?${queryString.stringify({
          lastEvaluatedKey: currentLastEvaluatedKey,
          expand: true,
        })}`,
        {
          method: "get",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            "x-client-id": clientId,
            Authorization: await getAccessJwtToken(),
          },
        }
      );
      if (!response.ok) {
        const body = await response.json();
        throw new HttpError(body.message || "Request failed", response.status);
      }

      const { policies, lastEvaluatedKey } = await response.json();

      currentLastEvaluatedKey = lastEvaluatedKey;
      allPolicies = [...allPolicies, ...policies];
    } while (currentLastEvaluatedKey);

    const sortedPolicies = allPolicies.sort((a: any, b: any) => {
      return a.createdAt > b.createdAt ? -1 : a.createdAt < b.createdAt ? 1 : 0;
    });

    return sortedPolicies;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * It fetches a policy from the API and returns the policy data
 * @param policyId - The id of the policy you want to get.
 * @param options -  supports "refresh".
 * @returns The policy object
 */
export const getPolicy = async (
  policyId: any,
  options: any = {},
  clientId: any
) => {
  const queryString =
    Object.keys(options).length > 0 ? `?${new URLSearchParams(options)}` : "";
  return await callAxleService(
    `${baseUrl}/policies/${policyId}${queryString}`,
    {
      method: "get",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": clientId,
        Authorization: await getAccessJwtToken(),
      },
    }
  );
};

export const validatePolicy = async (
  policy: any,
  clientId: any,
  accountId: any
) => {
  try {
    const res = await trackPromise(
      fetch(`${baseUrl}/policies/${policy}/validate`, {
        method: "post",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          "x-client-id": clientId,
          Authorization: await getAccessJwtToken(),
        },
        body: JSON.stringify({ account: accountId }),
      })
    );

    const body = await res.json();

    if (!res.ok) {
      throw new HttpError(body.message || "Request failed", res.status);
    }
    return body.data;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

export const getAvailablePolicyActions = async (
  policyId: any,
  clientId: any
) => {
  return await callAxleService(`${baseUrl}/policies/${policyId}/actions`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  });
};

export const executePolicyAction = async (
  policyId: any,
  actionType: any,
  clientId: any
) => {
  return await callAxleService(`${baseUrl}/policies/${policyId}/actions`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({ type: actionType }),
  });
};

/**
 * Get the completion status of a policy action
 * @param {string} policyActionId - The policy action ID
 * @param {string} clientId - The client ID
 * @returns {Promise<[{id: string, completionStatus: string, status?: string}, null] | [null, Error]>}
 */
export const getPolicyActionCompletionStatus = async (
  policyActionId: any,
  clientId: any
) => {
  return await callAxleService(
    `${baseUrl}/policies/actions/${policyActionId}/completion-status`,
    {
      method: "get",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": clientId,
        Authorization: await getAccessJwtToken(),
      },
    }
  );
};

export const getPendingPolicyActions = async (policyId: any, clientId: any) => {
  return await callAxleService(
    `${baseUrl}/policies/${policyId}/actions/pending`,
    {
      method: "get",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": clientId,
        Authorization: await getAccessJwtToken(),
      },
    }
  );
};

/**
 * Poll for policy action completion until completionStatus = "completed"
 * or max duration has passed (whichever happens first)
 * @param {string} policyActionId - The policy action ID
 * @param {string} clientId - The client ID
 * @param {number} pollIntervalMs - Polling interval in milliseconds (default: 15000)
 * @param {number} maxPollDurationMs - Max polling duration in milliseconds (default: MAX_POLL_DURATION_MS)
 * @returns {Promise<{id: string, completionStatus: string, status?: string}>}
 */
export const pollPolicyActionCompletion = async (
  policyActionId: any,
  clientId: any,
  pollIntervalMs: any = POLL_INTERVAL_MS,
  maxPollDurationMs: any = MAX_POLL_DURATION_MS
) => {
  return new Promise((resolve: any, reject: any) => {
    const startTime = Date.now();
    let timeoutId: any = null;

    const poll = async () => {
      // Check if max duration has passed
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime >= maxPollDurationMs) {
        console.error(
          `Polling timeout: Policy action ${policyActionId} did not complete within ${maxPollDurationMs / 1000
          } seconds`
        );
        reject(
          new Error(
            `Polling timeout: Policy action did not complete within ${maxPollDurationMs / 1000
            } seconds`
          )
        );
        return;
      }

      const [data, error] = await getPolicyActionCompletionStatus(
        policyActionId,
        clientId
      );

      if (error) {
        reject(error);
        return;
      }

      if (data.completionStatus === "completed") {
        resolve(data);
        return;
      }

      // Schedule next poll
      timeoutId = setTimeout(poll, pollIntervalMs);
    };

    poll();
  });
};

/**
 * Display toast messages based on policy action completion status.
 * Before dismissing the loading toast, signals completion so the progress
 * bar can animate to 100 %, then waits briefly before showing the result toast.
 *
 * @param {string} status - The final policy action status
 * @param {string} loadingToastId - The ID of the loading toast to dismiss
 */
const PROGRESS_COMPLETE_DELAY_MS = 500;

const showPolicyActionCompletionToast = (
  status: any,
  loadingToastId: any,
  maxPollDurationMs: any = MAX_POLL_DURATION_MS
) => {
  // Signal completion — the PolicyActionToast progress bar jumps to 100 %
  if (loadingToastId) {
    toast.loading(POLICY_ACTION_TOAST_MESSAGES.REQUEST_INITIATED, {
      id: loadingToastId,
      duration: Infinity,
      progressDuration: maxPollDurationMs,
      completed: true,
    } as any);
  }

  // Brief delay so the user sees the bar hit 100 %, then dismiss & show result
  setTimeout(() => {
    if (loadingToastId) {
      toast.dismiss(loadingToastId);
    }

    switch (status) {
      case POLICY_ACTION_STATUS.REQUEST_FAILED:
      case POLICY_ACTION_STATUS.EXPIRED:
        // Request failed or expired - show red toast
        toast.error(POLICY_ACTION_TOAST_MESSAGES.REQUEST_FAILED);
        break;

      case POLICY_ACTION_STATUS.REQUEST_SUCCEEDED:
        // Request succeeded but we're still waiting for policy refresh
        // This shouldn't typically be a final state, but handle it just in case
        toast.success(POLICY_ACTION_TOAST_MESSAGES.REQUEST_SUCCEEDED);
        break;

      case POLICY_ACTION_STATUS.POLICY_REFRESH_FAILED:
        // Show both messages: blue disappears first (top), red stays longer (bottom)
        toast.loading(POLICY_ACTION_TOAST_MESSAGES.REQUEST_SUCCEEDED, {
          duration: 2500,
        });
        setTimeout(() => {
          toast.error(POLICY_ACTION_TOAST_MESSAGES.POLICY_REFRESH_FAILED, {
            duration: 3000,
          });
        }, 1500);
        break;

      case POLICY_ACTION_STATUS.POLICY_REFRESH_SUCCEEDED:
        // Show both messages: blue disappears first (top), green stays longer (bottom)
        toast.loading(POLICY_ACTION_TOAST_MESSAGES.REQUEST_SUCCEEDED, {
          duration: 2500,
        });
        setTimeout(() => {
          toast.success(POLICY_ACTION_TOAST_MESSAGES.POLICY_REFRESH_SUCCEEDED, {
            duration: 3000,
          });
        }, 1500);
        break;

      default:
        // Unknown status - show generic error
        toast.error(POLICY_ACTION_TOAST_MESSAGES.REQUEST_FAILED);
        break;
    }
  }, PROGRESS_COMPLETE_DELAY_MS);
};

// Tracking for in-progress policy actions.
const policyActionsInProgress = new Set<any>();
const policyActionListeners = new Map<any, any>();

/** Check whether a given policy currently has an action in flight. */
export const isPolicyActionInProgress = (policyId: any) =>
  policyActionsInProgress.has(policyId);

/**
 * Subscribe to action-state changes for a policy.
 * The callback receives `true` when an action starts and `false` when it ends.
 * Returns an unsubscribe function (call it in useEffect cleanup).
 */
export const subscribeToPolicyAction = (policyId: any, callback: any) => {
  if (!policyActionListeners.has(policyId)) {
    policyActionListeners.set(policyId, new Set());
  }
  policyActionListeners.get(policyId).add(callback);
  return () => {
    const listeners = policyActionListeners.get(policyId);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) policyActionListeners.delete(policyId);
    }
  };
};

/** Internal helper — update the set and notify all subscribers. */
const notifyPolicyActionListeners = (policyId: any, inProgress: any) => {
  if (inProgress) {
    policyActionsInProgress.add(policyId);
  } else {
    policyActionsInProgress.delete(policyId);
  }
  const listeners = policyActionListeners.get(policyId);
  if (listeners) listeners.forEach((cb: any) => cb(inProgress));
};

/**
 * Poll a single policy action and handle the result: show completion toasts
 * and optionally fetch + forward the refreshed policy.
 *
 * @param {string} policyActionId - The policy action ID to poll
 * @param {string} policyId - The parent policy ID (for refresh fetch)
 * @param {string} clientId - The client ID
 * @param {string} loadingToastId - ID of the loading toast to dismiss/update
 * @param {Function|null} onPolicyRefreshSuccess - Optional callback for refreshed policy
 * @param {number} pollIntervalMs - Polling interval in ms
 * @param {number} maxPollDurationMs - Max polling duration in ms
 * @returns {Promise<void>}
 */
const pollAndHandlePolicyActionResult = (
  policyActionId: any,
  policyId: any,
  clientId: any,
  loadingToastId: any,
  onPolicyRefreshSuccess: any = null,
  pollIntervalMs: any = POLL_INTERVAL_MS,
  maxPollDurationMs: any = MAX_POLL_DURATION_MS
) => {
  return pollPolicyActionCompletion(
    policyActionId,
    clientId,
    pollIntervalMs,
    maxPollDurationMs
  )
    .then(async (result: any) => {
      console.log("Policy action completed:", result);
      showPolicyActionCompletionToast(
        result.status,
        loadingToastId,
        maxPollDurationMs
      );

      if (
        result.status === POLICY_ACTION_STATUS.POLICY_REFRESH_SUCCEEDED &&
        onPolicyRefreshSuccess &&
        typeof onPolicyRefreshSuccess === "function"
      ) {
        try {
          const [updatedPolicy, fetchError] = await getPolicy(
            policyId,
            undefined,
            clientId
          );
          if (!fetchError && updatedPolicy) {
            onPolicyRefreshSuccess(updatedPolicy);
          }
        } catch (fetchErr: any) {
          console.error("Failed to fetch updated policy:", fetchErr);
        }
      }
    })
    .catch((err: any) => {
      console.error("Polling error:", err);
      toast.dismiss(loadingToastId);
      toast.error(POLICY_ACTION_TOAST_MESSAGES.REQUEST_FAILED);
    });
};

/**
 * Execute a policy action and start polling for completion.
 * Shared function used by DetailsPage and PolicyValidation components.
 * Shows toast messages based on the policy action status.
 *
 * Action progress is tracked at module level so the UI stays correct even if
 * the React tree unmounts and remounts (e.g. the user navigates away and back).
 *
 * @param {string} policyId - The policy ID
 * @param {string} actionType - The action type to execute
 * @param {string} clientId - The client ID
 * @param {number} pollIntervalMs - Polling interval in milliseconds
 * @param {number} maxPollDurationMs - Max polling duration in milliseconds
 * @param {Function|null} onPolicyRefreshSuccess - Optional callback called with the updated policy when refresh succeeds
 * @returns {Promise<void>}
 */
export const executeAndPollPolicyAction = async (
  policyId: any,
  actionType: any,
  clientId: any,
  pollIntervalMs: any,
  maxPollDurationMs: any,
  onPolicyRefreshSuccess: any = null
) => {
  notifyPolicyActionListeners(policyId, true);

  const loadingToastId = toast.loading(
    POLICY_ACTION_TOAST_MESSAGES.REQUEST_INITIATED,
    { duration: Infinity, progressDuration: maxPollDurationMs } as any
  );

  const [data, error] = await executePolicyAction(
    policyId,
    actionType,
    clientId
  );

  if (error) {
    console.error("Failed to execute policy action:", error);
    toast.dismiss(loadingToastId);
    toast.error(POLICY_ACTION_TOAST_MESSAGES.REQUEST_FAILED);
    notifyPolicyActionListeners(policyId, false);
    return;
  }

  const policyActionId = data?.id;
  if (!policyActionId) {
    console.error("No policy action ID returned from executePolicyAction");
    toast.dismiss(loadingToastId);
    toast.error(POLICY_ACTION_TOAST_MESSAGES.REQUEST_FAILED);
    notifyPolicyActionListeners(policyId, false);
    return;
  }

  pollAndHandlePolicyActionResult(
    policyActionId,
    policyId,
    clientId,
    loadingToastId,
    onPolicyRefreshSuccess,
    pollIntervalMs,
    maxPollDurationMs
  ).finally(() => {
    notifyPolicyActionListeners(policyId, false);
  });
};

/**
 * Resume monitoring of pending policy actions detected on page load.
 * Shows persistent loading toasts, polls for completion, and re-enables buttons when done.
 */
const activePollingActions = new Set<any>();

export const resumePendingPolicyActions = async (
  policyId: any,
  clientId: any,
  onPolicyRefreshSuccess: any = null
) => {
  try {
    const [pendingActions, error] = await getPendingPolicyActions(
      policyId,
      clientId
    );

    if (error || !pendingActions || pendingActions.length === 0) {
      return;
    }

    const newActions = pendingActions.filter(
      (action: any) => !activePollingActions.has(action.id)
    );
    if (newActions.length === 0) {
      return;
    }

    newActions.forEach((action: any) => activePollingActions.add(action.id));
    notifyPolicyActionListeners(policyId, true);

    const pollPromises = newActions.map((action: any) => {
      const loadingToastId = toast.loading(
        POLICY_ACTION_TOAST_MESSAGES.REQUEST_INITIATED,
        { duration: Infinity, progressDuration: MAX_POLL_DURATION_MS } as any
      );

      return pollAndHandlePolicyActionResult(
        action.id,
        policyId,
        clientId,
        loadingToastId,
        onPolicyRefreshSuccess,
        POLL_INTERVAL_MS,
        MAX_POLL_DURATION_MS
      ).finally(() => {
        activePollingActions.delete(action.id);
      });
    });

    Promise.allSettled(pollPromises).then(() => {
      notifyPolicyActionListeners(policyId, false);
    });
  } catch (err: any) {
    console.error("Error resuming pending policy actions:", err);
  }
};

const refreshTokenRequest = async (
  selectedClientId: any,
  currentRefreshToken: any
) => {
  const res = await fetch(`${baseUrl}/token/refresh`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": selectedClientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({
      selectedClientId: selectedClientId,
      refreshToken: currentRefreshToken,
    }),
  });
  const body = await res.json();
  if (!res.ok) {
    throw new HttpError(body.message || "Request failed", res.status);
  }
  return body.data;
};

export const refreshTokenWithRetry = async (
  selectedClientId: any,
  currentRefreshToken: any
) => {
  try {
    console.log(`refreshing token for client: `, selectedClientId);
    const data = await trackPromise(
      refreshTokenRequest(selectedClientId, currentRefreshToken)
    );
    return data;
  } catch (error: any) {
    if (error instanceof HttpError && error.status === 502) {
      console.log("refreshToken returned a 502, retrying once");
      try {
        return await trackPromise(
          refreshTokenRequest(selectedClientId, currentRefreshToken)
        );
      } catch (retryError: any) {
        if (retryError instanceof HttpError) throw retryError;
        throw new HttpError(retryError.message || "Request failed", 500);
      }
    }
    if (error instanceof HttpError) throw error;
    throw new HttpError(error.message || "Request failed", 500);
  }
};

/**
 * It fetches an account from the API and returns the data
 * @param accountId - The account id of the account you want to get.
 * @param options -  supports "refresh".
 * @returns An object with the following properties:
 */
export const getAccount = async (
  accountId: any,
  options: any,
  clientId: any
) => {
  return await callAxleService(
    `${baseUrl}/accounts/${accountId}?${new URLSearchParams({ ...options })}`,
    {
      method: "get",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": clientId,
        Authorization: await getAccessJwtToken(),
      },
    }
  );
};

/**
 * It fetches the client data from the server and returns it
 * @returns The client object
 */
const CLIENT_CONFIG_TTL_MS = 10 * 60 * 1000; // 10 minutes

const getClientConfigCacheKey = (clientId: any, types: any) =>
  `clientConfig:${clientId}:${[...types].sort().join(",")}`;

const readClientConfigCache = (clientId: any, types: any) => {
  try {
    const raw = localStorage.getItem(getClientConfigCacheKey(clientId, types));
    if (!raw) return null;
    const { data, cachedAt } = JSON.parse(raw);
    if (Date.now() - cachedAt > CLIENT_CONFIG_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const writeClientConfigCache = (clientId: any, types: any, data: any) => {
  try {
    localStorage.setItem(
      getClientConfigCacheKey(clientId, types),
      JSON.stringify({ data, cachedAt: Date.now() })
    );
  } catch (error: any) {
    console.error("Error writing client config cache:", error);
  }
};

/** Drop cached GET /clients response so the sidebar picks up a new logoUrl immediately. */
export const invalidateClientConfigCache = (
  clientId: any,
  types: any = ["validation", "ignition"]
) => {
  try {
    localStorage.removeItem(getClientConfigCacheKey(clientId, types));
  } catch (error: any) {
    console.error("Error invalidating client config cache:", error);
  }
};

export const getClientConfig = async (
  types: any,
  clientId: any,
  readFromCache = true
) => {
  if (readFromCache) {
    const cached = readClientConfigCache(clientId, types);
    if (cached) {
      console.log(
        `Cache hit for client config: ${clientId} with types: ${types}`
      );
      return [cached, null];
    }
  }

  let urlParams = "";
  for (const configType of types) {
    urlParams += `&types=${configType}`;
  }
  const result = await callAxleService(`${baseUrl}/clients?${urlParams}`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  });

  const [data, error] = result;
  if (data && !error) writeClientConfigCache(clientId, types, data);

  return result;
};

/**
 * Sets logoUrl on the Axle client (admin / platform-admin only).
 * @returns {[data, error]} same shape as callAxleService
 */
export const updateClientConfigWithLogo = async (
  clientId: any,
  logoUrl: any
) => {
  return await callAxleService(`${baseUrl}/clients/logo`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: await getAccessJwtToken(),
      "x-client-id": clientId,
    },
    body: JSON.stringify({ logoUrl }),
  });
};

/**
 * Update the config and/or display name for a client. Omitted fields are left
 * untouched, so pass only what changed.
 * @param clientId - The client id
 * @param config - The config values to merge into the client's config
 * @param displayName - The client's new display name
 * @param targetClientId - The client to update, if not clientId itself; a
 *   platform client may update its destination clients.
 * @returns {[data, error]} same shape as callAxleService
 */
export const updateClientsConfig = async (
  clientId: string,
  config?: any,
  displayName?: string,
  targetClientId: string = clientId
) => {
  return await callAxleService(`${baseUrl}/clients`, {
    method: "PATCH",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: await getAccessJwtToken(),
      "x-client-id": clientId,
    },
    body: JSON.stringify([{ id: targetClientId, config, displayName }]),
  });
};

/**
 * Presigned PUT for uploading a client logo — dashboard counterpart of Ignition
 * `generateSignedUrl` in stacks/platform/ignition/src/lib/axle.ts, except it
 * goes to automatic instead of differential. Returns `{ signedUrl, logoUrl }`;
 * save `logoUrl` via updateClientConfigWithLogo after the PUT succeeds.
 * @returns {[data, error]} same shape as callAxleService
 */
export const generateSignedUrl = async (
  clientId: any,
  contentType: any
): Promise<any> => {
  return await callAxleService(`${baseUrl}/clients/logo/upload-url`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: await getAccessJwtToken(),
      "x-client-id": clientId,
    },
    body: JSON.stringify({ contentType }),
  });
};

export const getClientId = async () => {
  return await callAxleService(`${baseUrl}/client-id`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Authorization: await getAccessJwtToken(),
    },
  });
};

/**
 * Fetches a list of destination clients that a platform user can access
 * @returns An array of client objects with id, displayName, logoUrl etc.
 */
export const getDestinationClients = async (clientId: any) => {
  try {
    // Do not use trackPromise here — caller (DestinationClientProvider) tracks with a dedicated area
    // so this fetch does not block other UI (e.g. ignition sessions table) that use the default area
    const res = await fetch(
      `${local ? "http://localhost:4000/dev" : baseUrl}/platform/clients`,
      {
        method: "get",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: await getAccessJwtToken(),
          "x-client-id": clientId,
        },
      }
    );

    const body = await res.json();

    if (!res.ok) {
      throw new HttpError(body.message || "Request failed", res.status);
    }
    return body.data;
  } catch (error: any) {
    if (error instanceof HttpError) {
      throw error;
    }
    throw new HttpError(error.message || "Request failed", 500);
  }
};

const authHeaders = async () => ({
  "Content-Type": "application/json",
  Authorization: await getAccessJwtToken(),
});

const handleResponse = async (res: any) => {
  // A gateway timeout or error page has no JSON body, only a status.
  const raw = await res.text();
  let body: any = {};
  try {
    body = raw ? JSON.parse(raw) : {};
  } catch {
    console.error("Non-JSON response", { status: res.status });
  }
  if (!res.ok) {
    throw new HttpError(
      body.message || `Request failed (${res.status})`,
      res.status
    );
  }
  return body.data;
};

// --- In-memory cache for platform client list ---
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let clientsCache: any = null;
let clientsCacheTime = 0;

/**
 * List platform / standalone clients (destination clients are excluded).
 * Results are cached in-memory for 5 minutes.
 * @param {{ skipCache?: boolean }} options
 * @returns {Promise<Array<{id: string, displayName: string, entity: string}>>}
 */
export const listAllClients = async ({ skipCache = false }: any = {}) => {
  if (
    !skipCache &&
    clientsCache &&
    Date.now() - clientsCacheTime < CACHE_TTL_MS
  ) {
    return clientsCache;
  }

  const res = await fetch(`${baseUrl}/admin/clients`, {
    method: "get",
    mode: "cors",
    headers: await authHeaders(),
  });
  const data = await handleResponse(res);
  clientsCache = data;
  clientsCacheTime = Date.now();
  return data;
};

/** Evict the client list cache (called after mutations). */
export const invalidateClientsCache = () => {
  clientsCache = null;
  clientsCacheTime = 0;
};

/**
 * Fetch all destination clients for a platform client, including their full configs.
 * @param {string} platformClientId
 * @returns {Promise<Array<{id: string, displayName: string, config: object}>>}
 */
export const getDestinationClientsWithConfigs = async (
  platformClientId: any
) => {
  const res = await fetch(
    `${baseUrl}/admin/clients/${platformClientId}/destination-clients`,
    {
      method: "get",
      mode: "cors",
      headers: await authHeaders(),
    }
  );
  return handleResponse(res);
};

/**
 * Batch-update multiple destination clients with new config values.
 * Invalidates the client list cache on success.
 * @param {Array<{id: string, config: object}>} clients
 * @returns {Promise<{updated: object[], skipped: string[], failed: object[]}>}
 */
export const batchUpdateClients = async (clients: any) => {
  const res = await fetch(`${baseUrl}/admin/clients`, {
    method: "post",
    mode: "cors",
    headers: await authHeaders(),
    body: JSON.stringify(clients),
  });
  const data = await handleResponse(res);
  invalidateClientsCache();
  return data;
};

/**
 * Emails Axle support about a client config problem or request, as the user
 * confirmed it in Axle AI. Support is the only recipient; replies go to the
 * signed-in user.
 */
export const sendSupportEmail = async (
  clientId: string,
  email: { topic: SupportEmailTopic; subject: string; body: string }
): Promise<{ sent: boolean; to: string; subject: string }> => {
  const res = await fetch(`${baseUrl}/admin/ai/support-email`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify(email),
  });
  return handleResponse(res);
};

/**
 * Send the Axle AI conversation so far and get back the messages this turn
 * added, plus an action to confirm if Claude proposed one. Every tool acts on
 * clientId only.
 */
export const chatWithAxleAi = async (
  clientId: string,
  messages: AxleAiMessage[],
  model: AxleAiModel
): Promise<{
  messages: AxleAiMessage[];
  pendingAction?: PendingAction;
}> => {
  const res = await fetch(`${baseUrl}/admin/ai/chat`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify({ messages, model }),
  });
  return handleResponse(res);
};

/** The signed-in user's saved Axle AI chats for the client, newest first. */
export const listAxleAiChats = async (
  clientId: string
): Promise<AxleAiChatSummary[]> => {
  const res = await fetch(`${baseUrl}/admin/ai/chats`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  });
  return handleResponse(res);
};

export const getAxleAiChat = async (
  clientId: string,
  chatId: string
): Promise<AxleAiChat> => {
  const res = await fetch(`${baseUrl}/admin/ai/chats/${chatId}`, {
    method: "get",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
  });
  return handleResponse(res);
};

/** Saves the whole chat, replacing what was stored for it. */
export const saveAxleAiChat = async (
  clientId: string,
  { id, ...chat }: AxleAiChat
) => {
  const res = await fetch(`${baseUrl}/admin/ai/chats/${id}`, {
    method: "put",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-client-id": clientId,
      Authorization: await getAccessJwtToken(),
    },
    body: JSON.stringify(chat),
  });
  return handleResponse(res);
};
